import lodashCapitalize from 'lodash/capitalize';
import isBoolean from 'lodash/isBoolean';

import formatPrice from '../../services/formatPrice';

export type TGetCoverageVarStringOpts = {
  capitalize?: boolean;
  shouldParenthesize?: boolean;
  additionalTextDelimiter?: string;
  compactDisplay?: 'short' | 'long';
  notation?: 'compact' | 'standard';
};

// If you need to customize the text that comes at the end of a coverage var
// string for a specific coverage key, do that here.
// e.g., the 'Deductible' in '$100/$1,000 Deductible'
function getCoverageText(
  key: string | null | undefined,
  coverageText: string,
  shouldParenthesize: boolean
) {
  let text = coverageText;
  switch (key) {
    case 'collision_deductible':
      return 'Deductible';
    case 'medical_limit':
      text = coverageText.toLowerCase();
      break;
    case 'rentalReimbursement': {
      return coverageText.includes('max no daily limit')
        ? `No daily maximum`
        : '';
    }
    default: {
      // do nothing
    }
  }

  return shouldParenthesize && text ? `(${text})` : text;
}

function split(val: string, delimiter: string) {
  const delimiterIndex = val.indexOf(delimiter);
  if (delimiterIndex === -1) {
    return [val, ''];
  }
  const coverageValue = val.slice(0, delimiterIndex).trim();
  const optionalCoverageText = val.slice(delimiterIndex + 1).trim();

  return [coverageValue, optionalCoverageText];
}

/**
 * Get a human-readable string for a coverage variable
 * 1000 => $1,000
 * null => 'Not Included'
 * false => 'Not Included'
 * true => 'Included
 *
 * @param val The value to format.
 * @param key The key name.
 * @param opts The options.
 * @param opts.capitalize Flag that determines whether to capitalize non-number strings.
 * @param opts.shouldParenthesize Flag that determines whether to parenthesize extra info for numerical coverages.
 * @param opts.additionalTextDelimiter Set the delimiter used when extra text exists.
 * @return The formatted string.
 */
export function getCoverageVarString(
  val?: string | boolean | null,
  key?: string | null,
  opts?: TGetCoverageVarStringOpts
) {
  const {
    capitalize = true,
    shouldParenthesize = true,
    additionalTextDelimiter = '-',
    compactDisplay,
    notation,
  } = opts || {};

  if (isBoolean(val)) {
    return val ? 'Included' : 'Not Included';
  }
  if (!val) {
    return 'Not Included';
  }

  const numericCoverageRegex = /(\d+)\/?(\d+)?/;
  if (numericCoverageRegex.test(val)) {
    // handle situations where the coverage has a hyphened additional phrase
    // i.e. $100,000/$500,000 - Reduced By
    // In: '100000/500000 - Reduced By'
    // Out: ['100000/500000', 'Reduced By']
    const [coverageValue, optionalCoverageText] = split(
      val.toString(),
      additionalTextDelimiter
    );

    // In: '100000/500000'
    // Out: ['100000', '500000']
    const nums =
      coverageValue
        .toString()
        .match(numericCoverageRegex)
        ?.slice(1)
        .filter(Boolean) || [];

    // In: ['100000', '500000']
    // Out: '$100,000/$500,000'
    const shouldAbbreviateDigits =
      key === 'medical_limit' ? false : !!optionalCoverageText;
    const formattedNums = nums
      .map((el) =>
        formatPrice(el, {
          fractionDigits: 0,
          compactDisplay:
            compactDisplay || (shouldAbbreviateDigits ? 'short' : 'long'),
          notation:
            notation || (shouldAbbreviateDigits ? 'compact' : 'standard'),
        })
      )
      .join('/');

    const coverageText = getCoverageText(
      key,
      optionalCoverageText,
      shouldParenthesize
    );

    return coverageText ? `${formattedNums} ${coverageText}` : formattedNums;
  }

  return capitalize ? lodashCapitalize(val) : val;
}
